// src/services/riskAssessmentOverrideService.js
//
// Per-user override flags for the Risk Assessment scoring engine.
//
// Stored at:
//   users/{uid}/settings/riskAssessmentOverride
//
// Doc shape:
//   {
//     honorStatedProfile: boolean,   // true → suppress safety-rail caps
//     reason?: string,               // optional human-readable note
//     updatedAt: Timestamp
//   }
//
// COMPLIANCE NOTE
// ---------------
// Disabling safety-rail caps for a user is a serious decision. The caps
// exist to protect users who answered "no emergency fund" or "high crypto
// concentration + no stable income" — both real money-loss risk patterns
// the assessment was designed to catch.
//
// This override should be set MANUALLY in Firebase Console by an admin
// after a conscious decision. There is no UI for users to flip their own
// override — that would defeat the purpose.
//
// Default (doc missing OR honorStatedProfile !== true) is to enforce
// safety rails. This is the safe-by-default posture for all 355 users.

import { doc, getDoc } from "firebase/firestore";
import { db } from "../authentication/firebase-config";

const DEFAULT_OVERRIDE = Object.freeze({
  honorStatedProfile: false,
});

/**
 * Read the user's risk-assessment override flags from Firestore.
 * Returns the merged DEFAULT_OVERRIDE shape so callers can always
 * destructure without null-checks.
 */
export async function fetchRiskAssessmentOverride(uid) {
  if (!uid) return { ...DEFAULT_OVERRIDE };
  try {
    const snap = await getDoc(
      doc(db, "users", uid, "settings", "riskAssessmentOverride")
    );
    if (!snap.exists()) return { ...DEFAULT_OVERRIDE };
    return { ...DEFAULT_OVERRIDE, ...snap.data() };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      "[riskAssessmentOverrideService] read failed, defaulting to safe-by-default:",
      err.message
    );
    return { ...DEFAULT_OVERRIDE };
  }
}
